import React, { useEffect, useState } from 'react';
import { Container, Stack, Text, Button, Alert, Paper, Loader, Center } from '@mantine/core';
import { IconCheck, IconAlertCircle } from '@tabler/icons-react';
import { useSearchParams } from 'react-router-dom';
import { API_ENDPOINTS } from '../../config/api';

interface OptOutState {
  loading: boolean;
  success: boolean;
  error: string | null;
  message: string;
}

const OptOutPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<OptOutState>({
    loading: true,
    success: false,
    error: null,
    message: ''
  });

  const email = searchParams.get('email');

  useEffect(() => {
    const handleOptOut = async () => {
      if (!email) {
        setState({
          loading: false,
          success: false,
          error: 'Keine E-Mail-Adresse gefunden',
          message: ''
        });
        return;
      }

      try {
        const response = await fetch(`${API_ENDPOINTS.optOut}?email=${encodeURIComponent(email)}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        setState({
          loading: false,
          success: true,
          error: null,
          message: data.message || 'Sie wurden erfolgreich vom Newsletter abgemeldet.'
        });
      } catch (error) {
        console.error('Error during opt-out:', error);
        setState({
          loading: false,
          success: false,
          error: 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.',
          message: ''
        });
      }
    };

    handleOptOut();
  }, [email]);

  const handleBackToHome = () => {
    window.location.href = 'https://www.aclue.de';
  };

  if (state.loading) {
    return (
      <Container size="md" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
        <Paper
          p="xl"
          shadow="sm"
          radius="md"
          style={{ width: '100%', maxWidth: 500, margin: '0 auto' }}
        >
          <Center>
            <Stack gap="lg" align="center">
              <Loader size="lg" />
              <Text size="lg" fw={600}>
                Newsletter-Abmeldung wird bearbeitet...
              </Text>
              <Text size="md" ta="center" c="dimmed">
                Bitte warten Sie einen Moment.
              </Text>
            </Stack>
          </Center>
        </Paper>
      </Container>
    );
  }

  return (
    <Container size="md" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
      <Paper
        p="xl"
        shadow="sm"
        radius="md"
        style={{ width: '100%', maxWidth: 500, margin: '0 auto' }}
      >
        <Stack gap="xl" align="center">
          {state.success ? (
            <>
              <div
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: '#51cf66',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <IconCheck size={40} color="white" />
              </div>

              <Text size="xl" fw={700} ta="center">
                Erfolgreich abgemeldet
              </Text>

              <Text size="md" ta="center" c="dimmed">
                {state.message}
              </Text>

              <Text size="sm" ta="center" c="dimmed">
                Sie erhalten keine weiteren Newsletter von uns.
              </Text>

              <Alert color="green" variant="light" style={{ width: '100%' }}>
                <Text size="sm">
                  <strong>E-Mail-Adresse:</strong> {email}
                </Text>
              </Alert>
            </>
          ) : (
            <>
              <div
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: '#ff6b6b',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <IconAlertCircle size={40} color="white" />
              </div>

              <Text size="xl" fw={700} ta="center">
                Fehler bei der Abmeldung
              </Text>

              <Alert color="red" variant="light" style={{ width: '100%' }}>
                <Text size="sm">{state.error}</Text>
              </Alert>

              <Text size="sm" ta="center" c="dimmed">
                Falls das Problem weiterhin besteht, kontaktieren Sie uns bitte direkt.
              </Text>
            </>
          )}

          <Button
            onClick={handleBackToHome}
            size="md"
            fullWidth
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none'
            }}
          >
            Zur Aclue Website
          </Button>

          <Text size="xs" ta="center" c="dimmed">
            © 2025 Aclue GmbH - Ihr Partner für KI-Lösungen
          </Text>
        </Stack>
      </Paper>
    </Container>
  );
};

export default OptOutPage;
